import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
export interface CategoryProduct {
  name: string;
  price: string;
  image: string;
  hasConfigurator?: boolean;
  href?: string;
  badge?: string;
}
interface CategoryPageProps {
  /** H1 / category heading */
  title: string;
  /**
   * Category intro/description copy. Rendered inside `.category-description`
   * (also tagged `wf-category-description`) so WebsiteForge can dynamically
   * populate this region per category at import time. Pass plain text or
   * React nodes.
   */
  description: React.ReactNode;
  /** Optional breadcrumb label shown above the title (e.g. "Shop") */
  breadcrumbLabel?: string;
  /** Product listing data */
  products: CategoryProduct[];
}
/**
 * Reusable ecommerce category/product-listing template.
 *
 * One template renders every category page in WebsiteForge:
 * - /shop/                       (all products)
 * - /shop/gen-3/
 * - /shop/daedo-pss-system/
 * - /shop/daedo-products/
 * - /shop/wacoku-products/
 *
 * The `category-description` block sits directly below the H1 and above the
 * product grid, ready for WebsiteForge to inject the live category description.
 */
export function CategoryPage({
  title,
  description,
  breadcrumbLabel = 'Shop',
  products
}: CategoryPageProps) {
  return <div className="min-h-screen bg-white flex flex-col" data-id="element-147">
      <Header data-id="element-148" />

      <main className="flex-grow pt-32 pb-20" data-id="element-149">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-150">
          {/* Breadcrumb */}
          <nav className="text-sm text-gray-500 mb-4" aria-label="Breadcrumb" data-id="element-151">
            <a href="/" className="hover:text-gray-900" data-id="element-152">
              Home
            </a>
            <span className="mx-2" data-id="element-153">/</span>
            <a href="/shop/" className="hover:text-gray-900" data-id="element-154">
              {breadcrumbLabel}
            </a>
            <span className="mx-2" data-id="element-155">/</span>
            <span className="text-gray-900 font-medium" data-id="element-156">{title}</span>
          </nav>

          {/* Category Heading */}
          <h1 className="text-4xl sm:text-5xl font-black text-gray-900 uppercase tracking-tight mb-6" data-id="element-157">
            {title}
          </h1>

          {/*
           Category Description — WebsiteForge populates this dynamically
           per category. Keep the class names stable so the importer can
           target this region.
           */}
          <div className="category-description wf-category-description mb-12 max-w-4xl" data-id="element-158">
            <div className="prose prose-gray text-gray-700 text-base sm:text-lg leading-relaxed" data-id="element-159">
              {description}
            </div>
          </div>

          {/* Product Listing */}
          {products.length === 0 ? <div className="text-center py-16 border border-dashed border-gray-200 rounded-lg" data-id="element-160">
              <p className="text-gray-500" data-id="element-161">
                Products will appear here once this category is published.
              </p>
            </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-162">
              {products.map((product, index) => {
            const href = product.href ?? '#';
            return <article key={`${product.name}-${index}`} className="group flex flex-col bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow" data-id="element-163">
                    {/* Product image */}
                    <a href={href} className="block relative aspect-square bg-gray-50 overflow-hidden" data-id="element-164">
                      {product.badge && <span className="absolute top-3 left-3 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded" data-id="element-165">
                          {product.badge}
                        </span>}
                      {product.image.startsWith('http') ? <img src={product.image} alt={product.name} className="block w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300" data-id="element-166" /> : <div className="w-full h-full flex items-center justify-center text-xs text-gray-400 text-center px-4" data-id="element-167">
                          Product Image
                        </div>}
                    </a>

                    {/* Product info */}
                    <div className="flex flex-col flex-grow p-5" data-id="element-168">
                      <a href={href} className="flex-grow" data-id="element-169">
                        <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2 min-h-[3rem]" data-id="element-170">
                          {product.name}
                        </h3>
                      </a>
                      <div className="mb-4" data-id="element-171">
                        <p className="text-xl font-black text-gray-900" data-id="element-172">
                          {product.price}
                        </p>
                      </div>
                      {product.hasConfigurator ? <Button href={href} variant="primary" className="w-full !py-2 text-sm mt-auto" data-id="element-173">
                          Select Options
                        </Button> : <Button href={href} variant="primary" className="w-full !py-2 text-sm bg-red-600 hover:bg-red-700 focus:ring-red-600 mt-auto" data-id="element-174">
                          Add To Cart
                        </Button>}
                    </div>
                  </article>;
          })}
            </div>}
        </div>
      </main>

      <Footer data-id="element-175" />
    </div>;
}